import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, message, Select, Space, Table, TimePicker, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { DAY_OF_WEEK_LABELS } from "../../../model/facilities/FacilityScheduleEntry";

dayjs.extend(customParseFormat);

const { Text } = Typography;
const TIME_FORMAT = "HH:mm";

interface DraftEntry {
    // Stable per-row key for the table. Server-issued ids stay non-zero;
    // client-created rows use a negative counter so React diffs don't reuse
    // a row across days.
    key: number;
    dayOfWeek: number;
    openTime: string;
    closeTime: string;
}

interface FacilityOption {
    id: number;
    name: string;
    hasSchedule: boolean;
}

// Settings panel for editing a single facility's weekly open/close windows.
// Ignores SectionPanelProps's value/onChange because the section owns its own
// payload — the shell only renders the panel.
export const FacilitySchedulesSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();

    const [facilities, setFacilities] = useState<FacilityOption[] | null>(null);
    const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
    const [draft, setDraft] = useState<DraftEntry[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    // Source facility for the "copy schedule from" action. Cleared whenever the
    // target facility changes so it can never point at the facility being edited.
    const [copySourceId, setCopySourceId] = useState<number | null>(null);
    const [copying, setCopying] = useState(false);
    // Counter for client-side row keys. A ref (not state) so two synchronous
    // "Add" clicks in the same render cycle don't both read the same value
    // and produce colliding negative keys.
    const nextClientKey = useRef(-1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rows = await service.listFacilitiesForScheduleEditor();
                if (cancelled) return;
                setFacilities(rows);
                if (rows.length > 0) setSelectedFacilityId(rows[0].id);
            } catch (err) {
                if (cancelled) return;
                setLoadError(err instanceof Error ? err.message : "Failed to load facilities.");
            }
        })();
        return () => { cancelled = true; };
    }, [service]);

    const loadSchedule = useCallback(async (facilityId: number) => {
        try {
            const rows = await service.getFacilitySchedule(facilityId);
            setDraft(rows.map((r, idx) => ({
                key: r.id !== 0 ? r.id : -(idx + 1),
                dayOfWeek: r.dayOfWeek,
                openTime: r.openTime,
                closeTime: r.closeTime,
            } satisfies DraftEntry)));
            setActionError(null);
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to load schedule.");
        }
    }, [service]);

    useEffect(() => {
        setCopySourceId(null);
        if (selectedFacilityId == null) return;
        void loadSchedule(selectedFacilityId);
    }, [selectedFacilityId, loadSchedule]);

    const addWindow = useCallback((dayOfWeek: number) => {
        const key = nextClientKey.current;
        nextClientKey.current -= 1;
        setDraft(prev => [
            ...prev,
            { key, dayOfWeek, openTime: "09:00", closeTime: "17:00" },
        ]);
    }, []);

    const removeWindow = useCallback((key: number) => {
        setDraft(prev => prev.filter(e => e.key !== key));
    }, []);

    const updateWindow = useCallback((key: number, patch: Partial<DraftEntry>) => {
        setDraft(prev => prev.map(e => (e.key === key ? { ...e, ...patch } : e)));
    }, []);

    const hasInvalidEntry = useMemo(() =>
        draft.some(e => e.openTime >= e.closeTime), [draft]);

    const hasOverlap = useMemo(() => {
        // Grouped via plain object instead of Map to dodge CRA's tsconfig
        // target=es5 / downlevelIteration restriction on `for…of` over Map.
        const byDay: Record<number, DraftEntry[]> = {};
        draft.forEach(e => {
            (byDay[e.dayOfWeek] ??= []).push(e);
        });
        for (const day of Object.keys(byDay)) {
            const list = byDay[Number(day)];
            const sorted = [...list].sort((a, b) => a.openTime.localeCompare(b.openTime));
            for (let i = 1; i < sorted.length; i++) {
                // Adjacent (close == next open) is fine; strict overlap means
                // the next window starts *before* the previous one ends.
                if (sorted[i].openTime < sorted[i - 1].closeTime) return true;
            }
        }
        return false;
    }, [draft]);

    const handleSave = async () => {
        if (selectedFacilityId == null) return;
        setSaving(true);
        setActionError(null);
        try {
            const fresh = await service.replaceFacilitySchedule(selectedFacilityId, {
                entries: draft.map(e => ({
                    dayOfWeek: e.dayOfWeek,
                    openTime: e.openTime,
                    closeTime: e.closeTime,
                })),
            });
            setDraft(fresh.map(r => ({
                key: r.id,
                dayOfWeek: r.dayOfWeek,
                openTime: r.openTime,
                closeTime: r.closeTime,
            })));
            // Keep the copy-source filter accurate within the session: a facility
            // just saved with windows becomes a valid source; one saved empty no
            // longer is.
            setFacilities(prev => prev?.map(f =>
                f.id === selectedFacilityId ? { ...f, hasSchedule: fresh.length > 0 } : f) ?? prev);
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to save schedule.");
        } finally {
            setSaving(false);
        }
    };

    // Facilities the schedule can be copied from — every facility except the one
    // currently being edited, and only those that actually have a populated
    // schedule (copying from an empty one would just clear the editor).
    const copySourceOptions = useMemo(
        () => (facilities ?? [])
            .filter(f => f.id !== selectedFacilityId && f.hasSchedule)
            .map(f => ({ value: f.id, label: f.name })),
        [facilities, selectedFacilityId],
    );

    // Pulls the source facility's windows into the editor as fresh client-side
    // rows (negative keys) so they're treated as unsaved edits to the selected
    // facility. The user reviews them and clicks Save to persist — nothing is
    // written to the source or target by copying alone.
    const handleCopyFrom = useCallback(async () => {
        if (copySourceId == null || selectedFacilityId == null || copySourceId === selectedFacilityId) return;
        const sourceName = facilities?.find(f => f.id === copySourceId)?.name ?? "the selected facility";
        setCopying(true);
        setActionError(null);
        try {
            const rows = await service.getFacilitySchedule(copySourceId);
            setDraft(rows.map(r => {
                const key = nextClientKey.current;
                nextClientKey.current -= 1;
                return {
                    key,
                    dayOfWeek: r.dayOfWeek,
                    openTime: r.openTime,
                    closeTime: r.closeTime,
                } satisfies DraftEntry;
            }));
            if (rows.length === 0) {
                message.info(`${sourceName} has no open windows — the schedule was cleared. Save to apply.`);
            } else {
                message.success(
                    `Copied ${rows.length} window${rows.length === 1 ? "" : "s"} from ${sourceName}. Review and Save to apply.`);
            }
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to copy schedule.");
        } finally {
            setCopying(false);
        }
    }, [copySourceId, selectedFacilityId, service, facilities]);

    // Empties the editor. Non-destructive until Save — which persists the empty
    // schedule via the bulk-replace (the "closed all week" state).
    const clearSchedule = useCallback(() => {
        setDraft([]);
        message.info("Cleared all windows — Save to apply.");
    }, []);

    const columns = useMemo(() => [
        {
            title: "Day",
            dataIndex: "dayOfWeek" as const,
            render: (day: number) =>
                DAY_OF_WEEK_LABELS.find(d => d.value === day)?.label ?? `Day ${day}`,
        },
        {
            title: "Opens",
            dataIndex: "openTime" as const,
            render: (open: string, row: DraftEntry) => (
                <TimePicker
                    data-testid={`fs-open-${row.key}`}
                    value={dayjs(open, TIME_FORMAT)}
                    format={TIME_FORMAT}
                    minuteStep={15}
                    allowClear={false}
                    onChange={(value: Dayjs | null) =>
                        updateWindow(row.key, { openTime: (value ?? dayjs(open, TIME_FORMAT)).format(TIME_FORMAT) })
                    }
                />
            ),
        },
        {
            title: "Closes",
            dataIndex: "closeTime" as const,
            render: (close: string, row: DraftEntry) => (
                <TimePicker
                    data-testid={`fs-close-${row.key}`}
                    value={dayjs(close, TIME_FORMAT)}
                    format={TIME_FORMAT}
                    minuteStep={15}
                    allowClear={false}
                    onChange={(value: Dayjs | null) =>
                        updateWindow(row.key, { closeTime: (value ?? dayjs(close, TIME_FORMAT)).format(TIME_FORMAT) })
                    }
                />
            ),
        },
        {
            title: "",
            key: "actions",
            render: (_: unknown, row: DraftEntry) => (
                <Button
                    size="small"
                    danger
                    data-testid={`fs-remove-${row.key}`}
                    onClick={() => removeWindow(row.key)}
                >
                    Remove
                </Button>
            ),
        },
    ], [removeWindow, updateWindow]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="fs-load-error" />;
    }
    if (facilities === null) {
        return <Text type="secondary">Loading…</Text>;
    }
    if (facilities.length === 0) {
        return <Alert type="info" message="No facilities defined for this tenant yet." data-testid="fs-empty" />;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Space>
                <Text>Facility:</Text>
                <Select<number>
                    data-testid="fs-facility-select"
                    style={{ minWidth: 240 }}
                    value={selectedFacilityId ?? undefined}
                    onChange={(value: number) => setSelectedFacilityId(value)}
                    options={facilities.map(f => ({ value: f.id, label: f.name }))}
                />
            </Space>

            {copySourceOptions.length > 0 && (
                <Space wrap>
                    <Text type="secondary">Copy schedule from:</Text>
                    <Select<number>
                        data-testid="fs-copy-source-select"
                        style={{ minWidth: 240 }}
                        placeholder="Select a facility"
                        value={copySourceId ?? undefined}
                        onChange={(value: number) => setCopySourceId(value)}
                        options={copySourceOptions}
                    />
                    <Button
                        data-testid="fs-copy-button"
                        onClick={handleCopyFrom}
                        disabled={copySourceId == null || copying}
                        loading={copying}
                    >
                        Copy schedule
                    </Button>
                    <Text type="secondary">Loads the windows below — review, then Save.</Text>
                </Space>
            )}

            {actionError && (
                <Alert type="error" message={actionError} data-testid="fs-action-error" />
            )}
            {hasInvalidEntry && (
                <Alert
                    type="warning"
                    message="Each window's open time must be earlier than its close time."
                    data-testid="fs-invalid-range"
                />
            )}
            {hasOverlap && (
                <Alert
                    type="warning"
                    message="Two or more windows on the same day overlap."
                    data-testid="fs-overlap"
                />
            )}

            <Table
                rowKey="key"
                data-testid="fs-table"
                dataSource={[...draft].sort((a, b) =>
                    a.dayOfWeek - b.dayOfWeek || a.openTime.localeCompare(b.openTime))}
                columns={columns}
                pagination={false}
                locale={{ emptyText: "No open windows yet — add one per day this facility is open." }}
            />

            <Space wrap>
                {DAY_OF_WEEK_LABELS.map(d => (
                    <Button
                        key={d.value}
                        data-testid={`fs-add-${d.value}`}
                        onClick={() => addWindow(d.value)}
                    >
                        + {d.label}
                    </Button>
                ))}
            </Space>

            <Space>
                <Button
                    type="primary"
                    data-testid="fs-save"
                    onClick={handleSave}
                    disabled={hasInvalidEntry || hasOverlap || saving}
                    loading={saving}
                >
                    Save schedule
                </Button>
                <Button
                    danger
                    data-testid="fs-clear"
                    onClick={clearSchedule}
                    disabled={draft.length === 0 || saving}
                >
                    Clear schedule
                </Button>
            </Space>
        </Space>
    );
};
